import { Title } from '@solidjs/meta'
import { type Component, createSignal, onMount, Show } from 'solid-js'
import Button from '~/components/Button'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { useNavigate } from '~/router'
import { authService } from '~/services/auth.service'
import { companyService } from '~/services/company.service'
import { setAuthStore } from '~/stores/auth'
import { setCompanyStore } from '~/stores/company'

/**
 * Fin del flujo OAuth con redirect (GitHub): el backend llega aquí con
 * `?token=...&companyId=...` y se completa la sesión igual que en el login:
 * token + usuario + empresas + empresa activa.
 */
const OAuthCallbackPage: Component = () => {
  const navigate = useNavigate()
  const [error, setError] = createSignal('')

  onMount(async () => {
    const params = new URLSearchParams(window.location.search)

    const err = params.get('error')
    if (err) {
      setError(decodeURIComponent(err))
      window.history.replaceState({}, '', '/oauth-callback')
      return
    }

    const token = params.get('token')
    if (!token) {
      navigate('/login')
      return
    }

    try {
      const defaultCompanyId = params.get('companyId')
      setAuthStore('token', token)
      setAuthStore('companyId', defaultCompanyId)

      // Usuario (con permisos y isRoot)
      await authService.me()

      // Empresas del usuario + empresa activa
      const { items } = await companyService.list({ limit: 100 })
      const companies = items.map(c => ({
        company: {
          id: c.id,
          name: c.name,
          slug: c.slug,
          domain: c.domain ?? undefined,
          logo: c.logo ?? undefined
        },
        role: ((c as unknown as { role?: string }).role as string) ?? 'member',
        isDefault: c.id === defaultCompanyId
      }))
      setAuthStore('companies', companies)

      const chosen = companies.find(c => c.isDefault)?.company ?? companies[0]?.company
      if (chosen) {
        setCompanyStore({
          id: chosen.id,
          name: chosen.name,
          slug: chosen.slug,
          logo: chosen.logo ?? null,
          domain: chosen.domain ?? null
        })
        setAuthStore('companyId', chosen.id)
      }

      // Limpiar el token de la URL antes de entrar al panel
      window.history.replaceState({}, '', '/oauth-callback')
      navigate('/admin', { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión')
      window.history.replaceState({}, '', '/oauth-callback')
    }
  })

  return (
    <>
      <Title>Iniciando sesión | Florez Jaramillo</Title>
      <div class="h-[100dvh] w-full bg-background overflow-y-auto">
        <Container class="min-h-full py-8 flex items-center">
          <div class="w-full max-w-md mx-auto text-center">
            <Show fallback={<Icon icon="material-symbols-light:progress-activity" size={48} class="text-primary animate-spin mx-auto" />} when={error()}>
              <div class="mb-4">
                <Icon icon="material-symbols-light:error-outline" size={56} class="text-danger mx-auto" />
              </div>
              <h1 class="text-2xl font-bold text-foreground mb-2">No pudimos iniciar sesión</h1>
              <p class="text-foreground-subtle mb-6">{error()}</p>
              <Button color="primary" onClick={() => navigate('/login')}>
                Volver a iniciar sesión
              </Button>
            </Show>
            <Show when={!error()}>
              <h1 class="mt-4 text-xl font-semibold text-foreground">Completando inicio de sesión...</h1>
            </Show>
          </div>
        </Container>
      </div>
    </>
  )
}

export default OAuthCallbackPage
